import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowRight, FiCheck, FiCode, FiSmartphone, FiCpu, FiShoppingCart, FiCloud, FiSettings, FiChevronDown } from 'react-icons/fi';
import { Helmet } from "react-helmet";

const ServicesPage = () => {
    const [activeTab, setActiveTab] = useState('web');
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
        return () => setIsVisible(false);
    }, []);

    // Services Data


    const services = {
        web: {
            title: "Web Development",
            icon: <FiCode className="text-4xl text-blue-600" />,
            description: "We build high-performance, SEO-optimized websites that drive conversions and user engagement.",
            features: [
                "Custom React/Next.js Development",
                "Responsive & Mobile-First Design",
                "SEO Optimization",
                "E-Commerce Solutions",
                "CMS Integration (WordPress, Sanity)"
            ],
            stats: [
                { value: "3x", label: "Faster Load Times" },
                { value: "100%", label: "Mobile Responsive" },
                { value: "50+", label: "Websites Launched" },
                { value: "4.8/5", label: "Client Satisfaction" }
            ]
        },
        mobile: {
            title: "Mobile Development",
            icon: <FiSmartphone className="text-4xl text-purple-600" />,
            description: "Cross-platform mobile apps with seamless UX and cutting-edge performance.",
            features: [
                "React Native & Flutter Apps",
                "iOS & Android Development",
                "Push Notifications",
                "API Integration",
                "App Store Optimization"
            ],
            stats: [
                { value: "4.9/5", label: "App Store Rating" },
                { value: "2M+", label: "Downloads" },
                { value: "60%", label: "Faster Development" },
                { value: "4.8/5", label: "Client Satisfaction" }
            ]
        },
        game: {
            title: "Game Development",
            icon: <FiCpu className="text-4xl text-green-600" />,
            description: "Immersive 2D and 3D games with stunning graphics, smooth controls, and engaging gameplay.",
            features: [
                "Unity Engine Development",
                "Cross-Platform Support (Mobile, PC, Console)",
                "Multiplayer Integration",
                "In-App Purchases & Ads",
                "Custom Game Physics & AI"
            ],
            stats: [
                { value: "100+", label: "Games Published" },
                { value: "1M+", label: "Players Worldwide" },
                { value: "4.8/5", label: "Average Player Rating" },
                { value: "95%", label: "Retention Rate" }
            ]
        },
        shopify: {
            title: "Shopify Web Development",
            icon: <FiShoppingCart className="text-4xl text-emerald-600" />,
            description: "Launch and scale high-converting Shopify stores built for performance, UX, and brand identity.",
            features: [
                "Custom Shopify Theme Development",
                "Store Setup & Product Management",
                "Shopify App Integrations",
                "Payment Gateway Configuration",
                "Speed Optimization & Analytics"
            ],
            stats: [
                { value: "70%", label: "Conversion Rate Boost" },
                { value: "50+", label: "Stores Launched" },
                { value: "100%", label: "Mobile Optimized" },
                { value: "4.9/5", label: "Client Satisfaction" }
            ]
        },
        ai: {
            title: "AI & Automation Services",
            icon: <FiCloud className="text-4xl text-indigo-600" />,
            description: "Leverage the power of Artificial Intelligence to automate workflows, enhance customer experiences, and unlock business insights.",
            features: [
                "AI-Powered Chatbots & Virtual Assistants",
                "Machine Learning Model Development",
                "Predictive Analytics & Data Insights",
                "Process Automation & RPA",
                "Computer Vision & NLP Solutions"
            ],
            stats: [
                { value: "90%", label: "Process Efficiency Boost" },
                { value: "2x", label: "Faster Decision Making" },
                { value: "80%", label: "Cost Savings via Automation" },
                { value: "4.9/5", label: "Client Feedback" }
            ]
        }
    };


    const steps = [
        {
            step: "1",
            title: "Discovery",
            description: "Deep dive into your business goals and requirements to create a clear project roadmap.",
        },
        {
            step: "2",
            title: "Planning",
            description: "Create a detailed plan, set timelines, and allocate resources to ensure smooth project execution.",
        },
        {
            step: "3",
            title: "Design & Development",
            description: "Design user-friendly interfaces and develop high-performance applications tailored to your needs.",
        },
        {
            step: "4",
            title: "Testing & Quality Assurance",
            description: "Thorough testing to identify and fix any bugs, ensuring a seamless user experience.",
        },
        {
            step: "5",
            title: "Launch & Support",
            description: "Deploy the project and provide ongoing support to keep your systems running smoothly.",
        },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <Helmet>
                <title>Top Game & Web Development Services | UniTechnoStack</title>
                <meta
                    name="description"
                    content="Get professional game development, web design, mobile app development, and custom software solutions from UniTechnoStack. Build your next big idea with us."
                />
                <meta name="keywords" content="web development, mobile app development, game development, digital marketing, social media management, Shopify development" />
                <meta property="og:title" content="Top Game & Web Development Services | UniTechnoStack" />
                <meta property="og:description" content="Get professional game development, web design, mobile app development, and custom software solutions from UniTechnoStack." />
                <meta property="og:type" content="website" />
            </Helmet>

            {/* Hero Section */}
            <section className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-br from-blue-900 to-purple-900 flex items-center justify-center text-white min-h-screen">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-1/4 left-10 w-72 h-72 bg-purple-600 rounded-full mix-blend-soft-light filter blur-xl animate-pulse" />
                    <div className="absolute top-1/2 right-10 w-72 h-72 bg-cyan-500 rounded-full mix-blend-soft-light filter blur-xl animate-pulse animation-delay-2000" />
                    <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-blue-600 rounded-full mix-blend-soft-light filter blur-xl animate-pulse animation-delay-4000" />
                </div>
                {/* Floating Settings Icons Background */}
                <div className="absolute inset-0 overflow-hidden opacity-1000 pointer-events-none">
                    {[...Array(50)].map((_, i) => (
                        <motion.div
                            key={i}
                            animate={{
                                rotate: 360,
                                y: [0, Math.random() * 60 - 30],
                                x: [0, Math.random() * 40 - 20]
                            }}
                            transition={{
                                duration: 30 + Math.random() * 30,
                                repeat: Infinity,
                                ease: "linear",
                                repeatType: "loop"
                            }}
                            className="absolute text-blue-400/10"
                            style={{
                                fontSize: `${Math.random() * 24 + 16}px`,
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                            }}
                        >
                            <FiSettings className="w-full h-full" />
                        </motion.div>
                    ))}
                </div>
                <div className="md:container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                                Professional IT Services
                            </span>
                        </h1>
                        <p className="text-xl text-gray-200 mb-8">
                            We provide <strong>web development, mobile app development, game design, AI solutions, and Shopify development</strong>
                            — delivering high-performance, SEO-optimized, and scalable solutions tailored
                            to grow your business online.
                        </p>

                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-block"
                        >
                            <a
                                href="#explore"
                                className="bg-white text-blue-700 px-8 py-3 rounded-lg font-bold text-lg transition-colors flex items-center gap-2 hover:bg-gray-100"
                            >
                                Explore Services <FiArrowRight />
                            </a>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Subtle scroll indicator */}
                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
                    <FiChevronDown className="text-gray-400 w-8 h-8" />
                </div>

                {/* CSS for animations */}
                <style jsx>{`
    @keyframes float-slow {
      0%, 100% { transform: translate(0, 0) rotate(0deg); }
      50% { transform: translate(20px, 30px) rotate(5deg); }
    }
    @keyframes float-medium {
      0%, 100% { transform: translate(0, 0) rotate(0deg); }
      50% { transform: translate(-15px, -20px) rotate(-3deg); }
    }
    @keyframes gradient-shift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .animate-float-slow { animation: float-slow 15s ease-in-out infinite; }
    .animate-float-medium { animation: float-medium 12s ease-in-out infinite; }
    .animate-gradient-shift { 
      background-size: 200% 200%;
      animation: gradient-shift 8s ease infinite; 
    }
  `}</style>
            </section>

            {/* Services Showcase */}
            <section id="explore" className="py-16 px-4">
                <div className="md:container mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Expertise</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Discover our comprehensive range of services designed to elevate your digital presence
                        </p>
                    </div>

                    {/* Service Tabs */}
                    <div className="flex flex-wrap justify-center gap-2 mb-12">
                        {Object.keys(services).map((key) => (
                            <motion.button
                                key={key}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setActiveTab(key)}
                                className={`px-5 py-2 rounded-full font-medium transition-all ${activeTab === key
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                    }`}
                            >
                                {services[key].title}
                            </motion.button>
                        ))}
                    </div>

                    {/* Service Content */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
                        >
                            <div className="md:flex">
                                <div className="md:w-1/2 p-6 md:p-8 bg-gradient-to-br from-blue-50 to-purple-50">
                                    <div className="flex items-center gap-4 mb-6">
                                        {services[activeTab].icon}
                                        <h3 className="text-2xl md:text-3xl font-bold">{services[activeTab].title}</h3>
                                    </div>
                                    <p className="text-gray-700 mb-6 text-lg">
                                        {services[activeTab].description}
                                    </p>

                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        {services[activeTab].stats.map((stat, i) => (
                                            <motion.div
                                                whileHover={{ scale: 1.05 }}
                                                key={i}
                                                className="bg-white p-4 rounded-lg shadow-sm text-center"
                                            >
                                                <p className="text-xl md:text-2xl font-bold text-blue-600">{stat.value}</p>
                                                <p className="text-sm text-gray-600">{stat.label}</p>
                                            </motion.div>
                                        ))}
                                    </div>

                                    <motion.div
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="inline-block"
                                    >
                                        <a
                                            href="/quickenquiry"
                                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 hover:from-blue-700 hover:to-purple-700"
                                        >
                                            Get Started <FiArrowRight />
                                        </a>
                                    </motion.div>
                                </div>

                                <div className="md:w-1/2 p-6 md:p-8">
                                    <h3 className="text-xl font-bold mb-6">What's Included</h3>
                                    <ul className="space-y-3">
                                        {services[activeTab].features.map((feature, i) => (
                                            <motion.li
                                                key={i}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: i * 0.1 }}
                                                className="flex items-start gap-3"
                                            >
                                                <FiCheck className="text-green-500 mt-1 flex-shrink-0" />
                                                <span className="text-gray-700">{feature}</span>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            {/* Process Section */}
            <section className="py-16 px-4 bg-gray-50">
                <div className="md:container mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Our 5-Step Success Process</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            A structured approach to delivering exceptional results for every project
                        </p>
                    </div>

                    <div className="relative max-w-4xl mx-auto">
                        {/* Timeline line */}
                        <div className="absolute left-4 md:left-1/2 h-full w-1 bg-blue-200 transform -translate-x-1/2" />

                        <div className="space-y-12">
                            {steps.map((item, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.2 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    className="flex flex-col md:flex-row items-start relative"
                                >
                                    {/* Connector Dot */}
                                    <div className="flex justify-center md:absolute left-4 md:left-1/2 top-6 transform -translate-x-1/2 z-10">
                                        <div className="w-8 h-8 bg-blue-600 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white font-bold">
                                            {item.step}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'} mt-2 md:mt-0 ml-12 md:ml-0 ${index % 2 === 0 ? 'md:mr-auto' : 'md:ml-auto'}`}>
                                        <h3 className="text-xl font-bold mb-2 text-gray-800">{item.title}</h3>
                                        <p className="text-gray-600">{item.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-4">
                <div className="md:container mx-auto">
                    <motion.div
                        whileHover={{ scale: 1.01 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto shadow-xl"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
                        <p className="text-xl mb-8 max-w-2xl mx-auto">
                            Let's discuss how we can help you achieve your goals with our expertise.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <motion.a
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                href="/quickenquiry"
                                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
                            >
                                Get Free Consultation
                            </motion.a>
                            <motion.a
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                href="/portfolio"
                                className="inline-block border-2 border-white text-white px-8 py-3 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors"
                            >
                                View Our Work
                            </motion.a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default ServicesPage;